import { create } from "zustand";
import type { BulkSelection } from "@/lib/api/model/bulkSelection";
import type { PipelineFilterSchema } from "@/lib/api/model/pipelineFilterSchema";

interface PipelineViewState {
  /** Job the stage/filter/selection state currently applies to. */
  scopedJobId: string | null;
  /**
   * Bind view state to a job. Stage IDs are job-scoped — reusing one from a
   * previous job makes `/pipeline?stage_id=` return STAGE_NOT_FOUND. Call
   * during render before reading selectedStageId so the query never fires
   * with a foreign stage.
   */
  ensureJobScope: (jobId: string) => void;

  selectedStageId: string | null;
  setSelectedStageId: (id: string | null) => void;

  // Selection. `include` mode tracks explicit picks in `selectedCandidateIds`.
  // `all` mode is a virtual select-all of `selectionTotal` candidates minus
  // `excludedCandidateIds`; it ships a compact descriptor the backend resolves
  // against the live pipeline, so the client never fetches every candidate.
  bulkMode: "include" | "all";
  selectedCandidateIds: Set<string>;
  excludedCandidateIds: Set<string>;
  selectionTotal: number;
  toggleCandidate: (id: string) => void;
  selectAll: (ids: string[]) => void;
  /** Union `ids` into the current include-mode selection (stacks across pages). */
  mergeSelected: (ids: string[]) => void;
  selectAllCandidates: (total: number) => void;
  clearSelection: () => void;
  getSelectedCount: () => number;
  getBulkSelection: () => BulkSelection | null;

  filterSearch: string;
  setFilterSearch: (value: string) => void;

  /**
   * The filter-panel selections, mirrored from the URL by
   * `usePipelineFilterSync`. Kept here (rather than read from `nuqs` at each
   * call site) because bulk actions and exports are built outside React by
   * `buildPipelineActionBody()`, and they must ship the *same* filters the
   * table is showing — otherwise a select-all action would reach candidates
   * the user never saw.
   */
  filters: PipelineFilterSchema | null;
  /** Stable primitive that changes iff `filters` changes — safe as an effect dep. */
  filterSignature: string;
  setFilters: (filters: PipelineFilterSchema | null, signature: string) => void;
  clearFilters: () => void;
}

const NO_FILTERS_SIGNATURE = "";

function emptySelection() {
  return {
    bulkMode: "include" as const,
    selectedCandidateIds: new Set<string>(),
    excludedCandidateIds: new Set<string>(),
    selectionTotal: 0,
  };
}

export const usePipelineViewStore = create<PipelineViewState>()((set, get) => ({
  scopedJobId: null,
  ensureJobScope: (jobId) => {
    if (get().scopedJobId === jobId) return;
    set({
      scopedJobId: jobId,
      selectedStageId: null,
      // Reset the full selection (including a virtual `all` selection) so a
      // stale bulkMode/selectionTotal from the previous job can't leak across.
      bulkMode: "include",
      selectedCandidateIds: new Set(),
      excludedCandidateIds: new Set(),
      selectionTotal: 0,
      filterSearch: "",
      filters: null,
      filterSignature: NO_FILTERS_SIGNATURE,
    });
  },

  selectedStageId: null,
  setSelectedStageId: (id) =>
    set((s) =>
      s.selectedStageId === id
        ? s
        : { selectedStageId: id, ...emptySelection() },
    ),

  bulkMode: "include",
  selectedCandidateIds: new Set(),
  excludedCandidateIds: new Set(),
  selectionTotal: 0,
  toggleCandidate: (id) =>
    set((s) => {
      if (s.bulkMode === "all") {
        const next = new Set(s.excludedCandidateIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { excludedCandidateIds: next };
      }
      const next = new Set(s.selectedCandidateIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedCandidateIds: next };
    }),
  selectAll: (ids) =>
    set({
      bulkMode: "include",
      selectedCandidateIds: new Set(ids),
      excludedCandidateIds: new Set(),
      selectionTotal: 0,
    }),
  mergeSelected: (ids) =>
    set((s) => {
      // `all` mode means "everything except the exclusions", so merging is
      // un-excluding. Collapsing to an include-set here would shrink a
      // select-all of the whole pipeline down to just `ids`.
      if (s.bulkMode === "all") {
        const nextExcluded = new Set(s.excludedCandidateIds);
        for (const id of ids) nextExcluded.delete(id);
        return { excludedCandidateIds: nextExcluded };
      }
      const next = new Set(s.selectedCandidateIds);
      for (const id of ids) next.add(id);
      return {
        bulkMode: "include" as const,
        selectedCandidateIds: next,
        excludedCandidateIds: new Set<string>(),
        selectionTotal: 0,
      };
    }),
  selectAllCandidates: (total) =>
    set({
      bulkMode: "all",
      selectedCandidateIds: new Set(),
      excludedCandidateIds: new Set(),
      selectionTotal: total,
    }),
  clearSelection: () =>
    set({
      bulkMode: "include",
      selectedCandidateIds: new Set(),
      excludedCandidateIds: new Set(),
      selectionTotal: 0,
    }),
  getSelectedCount: () => {
    const s = get();
    if (s.bulkMode === "all") {
      return Math.max(s.selectionTotal - s.excludedCandidateIds.size, 0);
    }
    return s.selectedCandidateIds.size;
  },
  getBulkSelection: () => {
    const s = get();
    if (s.bulkMode === "all") {
      return { mode: "all", exclude_ids: Array.from(s.excludedCandidateIds) };
    }
    if (s.selectedCandidateIds.size === 0) return null;
    return { mode: "include", include_ids: Array.from(s.selectedCandidateIds) };
  },

  filterSearch: "",
  setFilterSearch: (value) =>
    set((s) =>
      s.filterSearch === value
        ? s
        : { filterSearch: value, ...emptySelection() },
    ),

  filters: null,
  filterSignature: NO_FILTERS_SIGNATURE,
  setFilters: (filters, signature) =>
    set((s) =>
      s.filterSignature === signature
        ? s
        : { filters, filterSignature: signature, ...emptySelection() },
    ),
  clearFilters: () =>
    set({
      filterSearch: "",
      filters: null,
      filterSignature: NO_FILTERS_SIGNATURE,
      ...emptySelection(),
    }),
}));
